import { useState, useCallback, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { trackMusicPlay } from "../../../lib/analytics";

interface TrackData {
  title: string;
  artist: string;
  art: string;
  album: string;
}

const FALLBACK_ART =
  "https://public.readdy.ai/ai/img_res/699741249c83e0c62c71a93a6ad3313e.jpg";

export default function ListenNow() {
  const { t } = useTranslation();
  const [isPlaying, setIsPlaying] = useState(false);
  const [showBlockedModal, setShowBlockedModal] = useState(false);
  const [currentTrack, setCurrentTrack] = useState<TrackData | null>(null);
  const [lastPlayed, setLastPlayed] = useState<TrackData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [trackTransition, setTrackTransition] = useState(false);
  const playerWindowRef = useRef<Window | null>(null);
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchNowPlaying = useCallback(async () => {
    try {
      const response = await fetch("https://api.live365.com/station/a11694");
      if (response.ok) {
        const data = await response.json();
        const track = data?.["current-track"];
        if (track) {
          setCurrentTrack((prev) => {
            const newTrack: TrackData = {
              title: track.title || "RanchFire Radio",
              artist: track.artist || "Live Stream",
              art: track.art || FALLBACK_ART,
              album: track.album || "",
            };
            if (prev && prev.title !== newTrack.title) {
              setTrackTransition(true);
              setTimeout(() => setTrackTransition(false), 600);
              if (prev.title !== "RanchFire Radio") {
                setLastPlayed((lp) => [prev, ...lp].slice(0, 3));
              }
            }
            return newTrack;
          });
        }
      }
    } catch (err) {
      // Silently fail — keep showing last known track
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchNowPlaying();
    pollIntervalRef.current = setInterval(fetchNowPlaying, 20000);
    return () => {
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
    };
  }, [fetchNowPlaying]);

  const openPlayerPopup = useCallback(() => {
    if (playerWindowRef.current && !playerWindowRef.current.closed) {
      playerWindowRef.current.focus();
      return;
    }

    const isMobile = window.innerWidth < 640;
    const width = isMobile ? 320 : 420;
    const height = isMobile ? 650 : 730;
    const left = (window.screen.width - width) / 2;
    const top = (window.screen.height - height) / 2;

    const popup = window.open(
      "",
      "RanchFireRadioPlayer",
      `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=no,toolbar=no,menubar=no,location=no,status=no`
    );

    if (popup) {
      const embedCode = isMobile
        ? '<iframe width="300" height="614" frameborder="0" src="https://live365.com/embeds/v1/player/a11694?s=sm&m=dark&c=mp3"></iframe>'
        : '<iframe width="400" height="700" frameborder="0" src="https://live365.com/embeds/v1/player/a11694?s=md&m=dark&c=mp3"></iframe>';

      popup.document.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <title>RanchFire Radio - Live Player</title>
            <style>
              body { margin: 0; padding: 0; overflow: hidden; background: #1a1a1a; display: flex; align-items: center; justify-content: center; min-height: 100vh; }
              iframe { display: block; border: none; }
            </style>
          </head>
          <body>${embedCode}</body>
        </html>
      `);
      popup.document.close();
      playerWindowRef.current = popup;
      setIsPlaying(true);

      const checkClosed = setInterval(() => {
        if (popup.closed) {
          setIsPlaying(false);
          clearInterval(checkClosed);
        }
      }, 1000);
    } else {
      setShowBlockedModal(true);
    }
  }, []);

  const handlePlatformClick = (platform: string) => {
    trackMusicPlay(platform);
  };

  const artworkUrl = currentTrack?.art || FALLBACK_ART;

  return (
    <section id="listen" className="relative overflow-hidden">
      {/* Dynamic Background — blurred album art */}
      <div className="absolute inset-0 z-0">
        <img
          src={artworkUrl}
          alt=""
          className="w-full h-full object-cover scale-110 blur-[80px] opacity-40 transition-all duration-1000"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-gray-950/80 via-gray-900/90 to-gray-950/95"></div>
      </div>

      {/* Truck Dashboard Overlay */}
      <div className="absolute inset-0 z-[5] pointer-events-none">
        <img
          src="https://readdy.ai/api/search-image?query=Interior%20view%20from%20driver%20seat%20of%20pickup%20truck%20dashboard%20with%20steering%20wheel%20and%20windshield%20showing%20open%20highway%20road%20ahead%20warm%20sunset%20lighting%20through%20windshield%20country%20road%20trip%20atmosphere%20realistic%20truck%20interior%20with%20radio%20visible%20on%20dashboard%20bokeh%20background%20cinematic%20lighting%20warm%20amber%20tones%20authentic%20americana%20aesthetic&width=1920&height=1080&seq=truck-dashboard-interior-001&orientation=landscape"
          alt=""
          className="w-full h-full object-cover opacity-15"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-gray-950/90 via-transparent to-gray-950/60"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 py-20 sm:py-28 lg:py-32">
        {/* Live Badge */}
        <div className="flex items-center justify-center gap-2 mb-10 sm:mb-14">
          <span className="relative flex h-3 w-3">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500"></span>
          </span>
          <span className="text-red-400 font-bold text-xs sm:text-sm uppercase tracking-[0.25em]">
            {t('listenNow.liveNow')}
          </span>
        </div>

        {/* Main Content Grid */}
        <div className="flex flex-col lg:flex-row items-center gap-10 lg:gap-16 xl:gap-20">
          {/* Album Artwork */}
          <div className="flex-shrink-0 w-64 h-64 sm:w-80 sm:h-80 lg:w-[400px] lg:h-[400px] relative group">
            {/* Glow effect behind artwork */}
            <div
              className="absolute -inset-4 rounded-3xl opacity-30 blur-2xl transition-all duration-1000"
              style={{
                background:
                  "radial-gradient(circle, rgba(217,119,6,0.5) 0%, transparent 70%)",
              }}
            ></div>

            {/* Vinyl record behind */}
            <div
              className={`absolute top-1/2 -translate-y-1/2 -right-8 sm:-right-12 lg:-right-16 w-56 h-56 sm:w-72 sm:h-72 lg:w-[360px] lg:h-[360px] rounded-full bg-gray-800 border-4 border-gray-700 z-0 ${
                isPlaying ? "animate-spin" : ""
              }`}
              style={{ animationDuration: "3s" }}
            >
              <div
                className="absolute inset-0 rounded-full"
                style={{
                  background:
                    "repeating-radial-gradient(circle at center, transparent 0px, transparent 8px, rgba(255,255,255,0.03) 9px, transparent 10px)",
                }}
              ></div>
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-amber-800 border-2 border-amber-700 flex items-center justify-center">
                <div className="w-3 h-3 rounded-full bg-gray-900"></div>
              </div>
            </div>

            {/* Main artwork */}
            <div
              className={`relative z-10 w-full h-full rounded-2xl lg:rounded-3xl overflow-hidden shadow-2xl shadow-black/50 transition-all duration-500 ${
                trackTransition ? "scale-95 opacity-80" : "scale-100 opacity-100"
              }`}
            >
              {isLoading ? (
                <div className="w-full h-full bg-gray-800 flex items-center justify-center">
                  <div className="w-12 h-12 border-4 border-amber-600 border-t-transparent rounded-full animate-spin"></div>
                </div>
              ) : (
                <img
                  src={artworkUrl}
                  alt={
                    currentTrack
                      ? `${currentTrack.title} by ${currentTrack.artist}`
                      : "RanchFire Radio"
                  }
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    (e.target as HTMLImageElement).src = FALLBACK_ART;
                  }}
                />
              )}
            </div>
          </div>

          {/* Metadata & Controls */}
          <div className="flex-1 text-center lg:text-left max-w-xl">
            {/* Now Playing Label */}
            <div className="flex items-center justify-center lg:justify-start gap-2 mb-4 sm:mb-6">
              {isPlaying && (
                <span className="flex gap-[3px] items-end h-4">
                  <span
                    className="w-[3px] bg-amber-500 rounded-full animate-pulse"
                    style={{ height: "60%", animationDelay: "0ms" }}
                  ></span>
                  <span
                    className="w-[3px] bg-amber-500 rounded-full animate-pulse"
                    style={{ height: "100%", animationDelay: "150ms" }}
                  ></span>
                  <span
                    className="w-[3px] bg-amber-500 rounded-full animate-pulse"
                    style={{ height: "40%", animationDelay: "300ms" }}
                  ></span>
                  <span
                    className="w-[3px] bg-amber-500 rounded-full animate-pulse"
                    style={{ height: "80%", animationDelay: "450ms" }}
                  ></span>
                </span>
              )}
              <span className="text-amber-500 font-bold text-xs uppercase tracking-[0.2em]">
                {isPlaying ? t('listenNow.nowPlaying') : t('listenNow.nowPlayingOn')}
              </span>
            </div>

            {/* Song Title */}
            <h2
              className={`text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold text-white mb-3 sm:mb-4 leading-tight transition-all duration-500 ${
                trackTransition
                  ? "translate-y-2 opacity-0"
                  : "translate-y-0 opacity-100"
              }`}
              style={{
                fontFamily: `"Bebas Neue", Impact, sans-serif`,
                letterSpacing: "0.02em",
              }}
            >
              {currentTrack?.title || "RanchFire Radio"}
            </h2>

            {/* Artist Name */}
            <p
              className={`text-xl sm:text-2xl lg:text-3xl text-amber-200/80 font-medium mb-2 transition-all duration-500 delay-75 ${
                trackTransition
                  ? "translate-y-2 opacity-0"
                  : "translate-y-0 opacity-100"
              }`}
            >
              {currentTrack?.artist || "Live Stream"}
            </p>

            {/* Album Name */}
            {currentTrack?.album && (
              <p
                className={`text-sm sm:text-base text-white/40 italic mb-8 sm:mb-10 transition-all duration-500 delay-100 ${
                  trackTransition
                    ? "translate-y-2 opacity-0"
                    : "translate-y-0 opacity-100"
                }`}
              >
                {currentTrack.album}
              </p>
            )}
            {!currentTrack?.album && <div className="mb-8 sm:mb-10"></div>}

            {/* Listen Live Button */}
            <div className="flex flex-col sm:flex-row items-center gap-4 mb-8 sm:mb-10">
              <button
                onClick={openPlayerPopup}
                className={`group relative px-10 sm:px-12 py-4 sm:py-5 rounded-full font-bold text-sm sm:text-base uppercase tracking-wider transition-all duration-300 cursor-pointer whitespace-nowrap flex items-center justify-center gap-3 overflow-hidden ${
                  isPlaying
                    ? "bg-amber-600 hover:bg-amber-500 text-white shadow-lg shadow-amber-600/30"
                    : "bg-gradient-to-r from-amber-700 to-orange-600 hover:from-amber-600 hover:to-orange-500 text-white shadow-xl shadow-amber-700/40"
                }`}
              >
                {/* Shimmer effect */}
                <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700"></span>
                <i
                  className={`${
                    isPlaying ? "ri-volume-up-line" : "ri-play-circle-fill"
                  } text-xl sm:text-2xl relative z-10`}
                ></i>
                <span className="relative z-10">
                  {isPlaying ? t('listenNow.openPlayer') : t('listenNow.listenLive')}
                </span>
              </button>

              <span className="text-white/30 text-xs sm:text-sm">
                {isPlaying ? t('listenNow.playingInWindow') : t('listenNow.streamInfo')}
              </span>
            </div>

            {/* Platform Links */}
            <div className="flex flex-wrap items-center justify-center lg:justify-start gap-3">
              <span className="text-white/30 text-xs uppercase tracking-wider mr-2">
                {t('listenNow.alsoOn')}
              </span>
              <a
                href="#"
                onClick={() => handlePlatformClick("Spotify")}
                className="px-4 py-2 bg-white/5 hover:bg-white/10 border border-white/10 hover:border-white/20 text-white/70 hover:text-white text-sm rounded-full font-medium transition-all duration-300 whitespace-nowrap cursor-pointer flex items-center gap-2"
              >
                <i className="ri-spotify-line text-base"></i>Spotify
              </a>
              <a
                href="#"
                onClick={() => handlePlatformClick("Apple Music")}
                className="px-4 py-2 bg-white/5 hover:bg-white/10 border border-white/10 hover:border-white/20 text-white/70 hover:text-white text-sm rounded-full font-medium transition-all duration-300 whitespace-nowrap cursor-pointer flex items-center gap-2"
              >
                <i className="ri-apple-line text-base"></i>Apple Music
              </a>
              <a
                href="#"
                onClick={() => handlePlatformClick("TuneIn")}
                className="px-4 py-2 bg-white/5 hover:bg-white/10 border border-white/10 hover:border-white/20 text-white/70 hover:text-white text-sm rounded-full font-medium transition-all duration-300 whitespace-nowrap cursor-pointer flex items-center gap-2"
              >
                <i className="ri-radio-line text-base"></i>TuneIn
              </a>
            </div>
          </div>
        </div>

        {/* Recently Played */}
        {lastPlayed.length > 0 && (
          <div className="mt-16 sm:mt-20">
            <h3 className="text-white/40 text-xs uppercase tracking-[0.2em] font-bold mb-6 text-center lg:text-left">
              {t('listenNow.recentlyPlayed')}
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {lastPlayed.map((track, idx) => (
                <div
                  key={`${track.title}-${idx}`}
                  className="flex items-center gap-4 bg-white/5 backdrop-blur-sm border border-white/5 rounded-xl p-3 sm:p-4 hover:bg-white/8 transition-all duration-300"
                >
                  <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-lg overflow-hidden flex-shrink-0">
                    <img
                      src={track.art || FALLBACK_ART}
                      alt={track.title}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = FALLBACK_ART;
                      }}
                    />
                  </div>
                  <div className="min-w-0">
                    <p className="text-white text-sm font-semibold truncate">
                      {track.title}
                    </p>
                    <p className="text-white/50 text-xs truncate">{track.artist}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Features Strip */}
        <div className="mt-16 sm:mt-20 grid grid-cols-3 gap-4 sm:gap-8 max-w-3xl mx-auto">
          <div className="text-center">
            <div className="w-12 h-12 sm:w-14 sm:h-14 flex items-center justify-center mx-auto mb-3 bg-amber-700/20 rounded-full border border-amber-700/30">
              <i className="ri-music-2-line text-xl sm:text-2xl text-amber-500"></i>
            </div>
            <h4 className="font-bold text-white text-sm sm:text-base mb-1">{t('listenNow.feature.live')}</h4>
            <p className="text-xs text-white/40">{t('listenNow.feature.liveDesc')}</p>
          </div>
          <div className="text-center">
            <div className="w-12 h-12 sm:w-14 sm:h-14 flex items-center justify-center mx-auto mb-3 bg-amber-700/20 rounded-full border border-amber-700/30">
              <i className="ri-headphone-line text-xl sm:text-2xl text-amber-500"></i>
            </div>
            <h4 className="font-bold text-white text-sm sm:text-base mb-1">{t('listenNow.feature.hd')}</h4>
            <p className="text-xs text-white/40">{t('listenNow.feature.hdDesc')}</p>
          </div>
          <div className="text-center">
            <div className="w-12 h-12 sm:w-14 sm:h-14 flex items-center justify-center mx-auto mb-3 bg-amber-700/20 rounded-full border border-amber-700/30">
              <i className="ri-heart-line text-xl sm:text-2xl text-amber-500"></i>
            </div>
            <h4 className="font-bold text-white text-sm sm:text-base mb-1">{t('listenNow.feature.adFree')}</h4>
            <p className="text-xs text-white/40">{t('listenNow.feature.adFreeDesc')}</p>
          </div>
        </div>
      </div>

      {/* Fallback Modal */}
      {showBlockedModal && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl p-6 sm:p-8 max-w-sm w-full shadow-2xl text-center">
            <div className="w-14 h-14 flex items-center justify-center mx-auto mb-4 bg-amber-100 rounded-full">
              <i className="ri-volume-up-line text-3xl text-amber-700"></i>
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">{t('listenNow.popupBlocked')}</h3>
            <p className="text-sm text-gray-600 mb-6">
              {t('listenNow.popupBlockedDesc')}
            </p>
            <div className="flex flex-col gap-3">
              <a
                href="https://live365.com/embeds/v1/player/a11694?s=xl&m=dark&c=mp3"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => {
                  setIsPlaying(true);
                  setShowBlockedModal(false);
                }}
                className="w-full py-3 bg-amber-700 hover:bg-amber-600 text-white rounded-xl font-bold text-sm uppercase tracking-wider transition-colors cursor-pointer whitespace-nowrap inline-flex items-center justify-center gap-2"
              >
                <i className="ri-external-link-line text-lg"></i>
                {t('listenNow.openPlayer')}
              </a>
              <button
                onClick={() => setShowBlockedModal(false)}
                className="w-full py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-medium text-sm transition-colors cursor-pointer whitespace-nowrap"
              >
                {t('listenNow.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
